// Procedural SFX + level-specific ambience using Web Audio API.
let ctx = null;
let muted = false;

// Persistent ambient sources
let ambient = { osc: null, gain: null, filter: null, lfo: null, lfoGain: null, active: false, level: null };

function getCtx() {
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
    } catch (e) {
      ctx = null;
    }
  }
  return ctx;
}

export function toggleMute(state) {
  muted = state;
  if (ambient.gain) ambient.gain.gain.value = muted ? 0 : ambient.baseVol || 0.03;
}
export function isMuted() {
  return muted;
}

function beep({ freq = 440, dur = 0.1, type = "square", vol = 0.15, slideTo = null, filterFreq = null }) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  let node = osc;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, c.currentTime);
  if (slideTo !== null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), c.currentTime + dur);
  }
  if (filterFreq) {
    const bp = c.createBiquadFilter();
    bp.type = "lowpass";
    bp.frequency.value = filterFreq;
    node.connect(bp);
    node = bp;
  }
  gain.gain.setValueAtTime(vol, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  node.connect(gain).connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + dur);
}

// Noise burst (for crash / footstep)
function noiseBurst({ dur = 0.15, vol = 0.15, filterFreq = 800, filterType = "lowpass" }) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const bufferSize = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = filterFreq;
  const gain = c.createGain();
  gain.gain.setValueAtTime(vol, c.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  src.connect(filter).connect(gain).connect(c.destination);
  src.start();
  src.stop(c.currentTime + dur);
}

export const sfx = {
  step: () => {
    // soft "tap" footstep
    noiseBurst({ dur: 0.06, vol: 0.05, filterFreq: 600, filterType: "bandpass" });
  },
  pickup: () => {
    beep({ freq: 660, dur: 0.08, type: "square", vol: 0.14 });
    setTimeout(() => beep({ freq: 990, dur: 0.1, type: "square", vol: 0.14 }), 60);
    setTimeout(() => beep({ freq: 1320, dur: 0.12, type: "triangle", vol: 0.1 }), 130);
  },
  hit: () => {
    beep({ freq: 220, dur: 0.35, type: "sawtooth", vol: 0.22, slideTo: 60 });
    noiseBurst({ dur: 0.2, vol: 0.15, filterFreq: 400 });
  },
  crash: () => {
    beep({ freq: 140, dur: 0.5, type: "sawtooth", vol: 0.28, slideTo: 40 });
    setTimeout(() => beep({ freq: 90, dur: 0.4, type: "square", vol: 0.2, slideTo: 30 }), 80);
    noiseBurst({ dur: 0.5, vol: 0.22, filterFreq: 900 });
    setTimeout(() => noiseBurst({ dur: 0.3, vol: 0.12, filterFreq: 300 }), 120);
  },
  win: () => {
    [523, 659, 784, 1046].forEach((f, i) =>
      setTimeout(() => beep({ freq: f, dur: 0.14, type: "triangle", vol: 0.18 }), i * 110)
    );
  },
  tireScreech: () => {
    // Enemy screech: high-pitched noise burst
    const muffle = ambient.muffled ? { vol: 0.06, filterFreq: 900 } : { vol: 0.14, filterFreq: 2400 };
    noiseBurst({ dur: 0.4, vol: muffle.vol, filterFreq: muffle.filterFreq, filterType: "bandpass" });
  },
  hornHonk: () => {
    const v = ambient.muffled ? 0.07 : 0.16;
    beep({ freq: 320, dur: 0.18, type: "square", vol: v, filterFreq: ambient.muffled ? 500 : null });
    setTimeout(() => beep({ freq: 260, dur: 0.24, type: "square", vol: v, filterFreq: ambient.muffled ? 500 : null }), 80);
  },
  click: () => beep({ freq: 880, dur: 0.05, type: "square", vol: 0.12 }),

  // ===== FOG / WEATHER MUFFLE =====
  // When fog weather is active, dampen the ambient bed by shifting the lowpass
  // cutoff down and cutting gain. Call `setMuffled(true|false)` per run.
  setMuffled: (muffled) => {
    if (!ambient.active || !ambient.gain || !ambient.filter) return;
    const target = muffled ? 120 : (ambient.level === "outdoor" ? 420 : 220);
    const volTarget = muffled ? ambient.baseVol * 0.35 : ambient.baseVol;
    try {
      const c = getCtx();
      const t = c.currentTime;
      ambient.filter.frequency.cancelScheduledValues(t);
      ambient.filter.frequency.linearRampToValueAtTime(target, t + 0.35);
      ambient.gain.gain.cancelScheduledValues(t);
      ambient.gain.gain.linearRampToValueAtTime(volTarget, t + 0.35);
    } catch (e) { /* noop */ }
    ambient.muffled = !!muffled;
  },

  // ===== AMBIENT =====
  startAmbient: (level) => {
    if (muted) return;
    const c = getCtx();
    if (!c) return;
    if (ambient.active && ambient.level === level) return;
    if (ambient.active) sfx.stopAmbient();

    // Outdoor: airy hum + occasional birds/wind; Underground: low rumble + drone
    const osc = c.createOscillator();
    const gain = c.createGain();
    const filter = c.createBiquadFilter();
    // Slow LFO to modulate volume for a "living" ambient
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();

    if (level === "outdoor") {
      osc.type = "sawtooth";
      osc.frequency.value = 65;
      filter.type = "lowpass";
      filter.frequency.value = 420;
      lfo.frequency.value = 0.15;
      lfoGain.gain.value = 0.012;
      ambient.baseVol = 0.022;
    } else {
      osc.type = "sawtooth";
      osc.frequency.value = 42;
      filter.type = "lowpass";
      filter.frequency.value = 220;
      lfo.frequency.value = 0.08;
      lfoGain.gain.value = 0.02;
      ambient.baseVol = 0.045;
    }

    gain.gain.value = ambient.baseVol;
    lfo.connect(lfoGain).connect(gain.gain);
    osc.connect(filter).connect(gain).connect(c.destination);
    osc.start();
    lfo.start();

    ambient = { osc, gain, filter, lfo, lfoGain, active: true, level, baseVol: ambient.baseVol };
  },

  stopAmbient: () => {
    if (!ambient.active) return;
    try {
      ambient.osc.stop();
      ambient.lfo.stop();
    } catch (e) { /* noop */ }
    ambient = { osc: null, gain: null, filter: null, lfo: null, lfoGain: null, active: false, level: null };
  },
};

export function resumeAudio() {
  const c = getCtx();
  if (c && c.state === "suspended") c.resume();
}
